import { get, writable } from 'svelte/store';
import { debounce, throttle } from 'throttle-debounce';
import { API_BASE_URI } from '$lib/components/env';

import type { Warning } from '@api/types';
import { toastsStore } from '@components/ui/toasts';
import { userStore } from '@components/user/store';
import type { BlockedUser } from '@components/user-relation/types';
import { isPaymentPage } from '@shared/routing';
import { type paths } from '@shared/swagger';

import type { onSocketEventCallback, socketResponse, Store, StoreState } from './types';

import { browser } from '$app/environment';
import { io, Socket } from 'socket.io-client';

let disconnectTimer: number | null = null;
const SOCKET_RECONNECT_CHECK = 1 * 1000;
export let lastMessages: string[][] = [];
export function clearLastMessages() {
    lastMessages = [];
}

export function debug(event: string, ...args: unknown[]) {
    lastMessages.push([(new Date()).toISOString(), event, JSON.stringify(args).substring(0, 150)]);
    while (lastMessages.length > 20) {
        lastMessages.shift();
    }
}

export const initialValues: StoreState = {
    isConnected: false,
};

function initSocket(): Store {
    const store = writable<StoreState>(initialValues);
    const { subscribe, update, set } = store;
    const updateStore = (data: Partial<StoreState>) => {
        update((state) => {
            return {
                ...state,
                ...data,
            };
        });
    };

    let client: Socket | undefined = undefined;

    const subscribeConnect = () => {
        client?.on('connect', () => {
            console.log('Connected');
            updateStore({
                isConnected: true,
            });
        });

        client?.on('disconnect', (reason: string) => {
            console.log('Disconnected');
            updateStore({
                isConnected: false,
            });
            checkConnection();
            debug('disconnect');
        });
    };

    const getClient = (): Socket => {
        if (isPaymentPage()) {
            throw new Error('Socket forbidden');
        }
        if (!browser || !userStore.isAuth()) {
            throw new Error('Socket init error');
        }
        if (!client) {
            client = io(API_BASE_URI, {
                withCredentials: true,
                autoConnect: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 3000,
                transports: ['websocket', 'polling', 'webtransport'],
                query: {
                    url: document.location.href,
                    version: '1.02',
                },
            });
            window.onerror = (e: Event | string) => debug(JSON.stringify(e));

            client.onAny((event, ...args) => {
                debug(event, args);
                if (event !== '/room/ping' && event !== '/error/session-exists' && event !== '/error/socket-exists') {
                    checkConnection();
                }
                if (event === 'exception') {
                    debug(args.length > 0 ? args[0]?.message : 'Unknown error');
                    toastsStore.error(args.length > 0 ? args[0]?.message : 'Unknown error');
                }
            });
            subscribeConnect();
            client.connect();
        }
        return client;
    };

    const disconnect = () => {
        client?.disconnect();
    };

    const reconnect = () => {
        if (client && !client.active) {
            client.connect();
        }
    };

    const checkRestart = () => {
        if (disconnectTimer) {
            window.clearInterval(disconnectTimer);
        }
        disconnectTimer = window.setInterval(reconnect, SOCKET_RECONNECT_CHECK);
    };

    const once = <P extends keyof paths>(event: P | 'connect', callback: onSocketEventCallback<P>) => {
        getClient().once(event as string, callback);
    };

    const on = <P extends keyof paths>(event: P | 'connect', callback: onSocketEventCallback<P>) => {
        getClient().on(event as string, data => {
            callback(data);
        });
    };

    const off = <P extends keyof paths>(event: P | 'connect') => {
        getClient().off(event as string);
    };

    const throttleEmit: <P extends keyof paths>(event: P, data?: unknown) => void
        = throttle(500, (p, v) => { debug(p, v); client?.emit(p, v); });

    const debounceEmit: <P extends keyof paths>(event: P, data?: unknown) => void
        = debounce(500, (p, v) => { debug(p, v); client?.emit(p, v); });

    const emitNoResponse = <P extends keyof paths>(event: P, data?: unknown): void => {
        const cl = getClient();
        if (!cl.connected) {
            console.log('Client is not connected, event:', event);
            cl.once('connect', () => {
                debug(event, data);
                console.log('Sending event after reconnect, event: ' + event + ' data: ' + JSON.stringify(data));
                cl.emit(event as string, data);
            });
            cl.connect();
        } else {
            debug(event, data);
            cl.emit(event as string, data);
        }
    };

    const emitWithResponse = <P extends keyof paths>(event: P, data?: unknown): Promise<socketResponse<paths[P]>> => {
        return new Promise((resolve, reject) => {
            const cl = getClient();
            if (!cl.connected) {
                cl.once('connect', () => {
                    debug(event, data);
                    cl.emit(event as string, data, (val: socketResponse<paths[P]>) => {
                        resolve(val);
                    });
                });
                cl.connect();
            } else {
                debug(event, data);
                cl.emit(event as string, data, (val: socketResponse<paths[P]>) => {
                    resolve(val);
                });
            }
        });
    };
    const checkConnection = () => {
        reconnect();
        checkRestart();
    };

    return {
        subscribe, update, set,
        getState: () => {
            return get(store);
        },
        checkConnection,
        onReconnect: (callback: () => void) => {
            if (getClient().connected) {
                callback();
                on('connect', callback);
            } else {
                once('connect', () => {
                    callback();
                    on('connect', callback);
                });
            }
        },
        onErrorUserBlocked: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            on('/error/user/blocked', callback);
        },
        onErrorMessagesTranslation: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            on('/message-notification/error-translation', callback);
        },
        onPing: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            on('/room/ping', callback);
        },
        onRoomFound: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            on('/event/room/found', callback);
        },
        onRoomClosed: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            once('/event/room/closed', callback);
        },
        onRoomSkipped: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            once('/event/room/skipped', callback);
        },
        onMessageReceived: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            on('/message-notification/received', callback);
        },
        onMessageTranslated: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            on('/message-notification/translated', callback);
        },
        onSearchTimedOut: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            on('/event/redirect-to-start-room', callback);
        },
        onPartnerUpdated: <P extends keyof paths>(callback: onSocketEventCallback<P>) => {
            on('/player/partner/updated', callback);
        },
        onSessionExists: (callback: () => void) => {
            on('/error/session-exists', callback);
        },
        disconnect: () => {
            client?.off();
            client?.disconnect();
            client = undefined;
        },
        offAll: () => {
            client?.off();
            subscribeConnect();
        },
        // eslint-disable-next-line
        // @ts-ignore
        removeListener: (event, listener) => {
            client?.removeListener(event, listener);
        },
        call: {
            onIncoming: (
                cb: (data: {
                    from: string;
                    roomId: string;
                    hasSubscription: boolean;
                    haveTransactionLastYear: boolean;
                    partnerId: string;
                }) => void
            ) => {
                on('/call/notification', cb);
            },
            onClosed: (cb: (data: { closedBy: string; conversationId?: string }) => void) => {
                on('/call/closed', cb);
            },
        },
        user: {
            onAgeVerify: (cb: (data: { url: string }) => void) => {
                on('/user/age/verify', cb);
            },
            onBlocked: (cb: (data: { user: BlockedUser }) => void) => {
                on('/user/blocked', cb);
            },
            onUnblocked: (cb: (data: { userId: string; blockerId: string }) => void) => {
                on('/user/unblocked', cb);
            },
            onWarnReceived: (cb: (data: Warning) => void) => {
                on('/user/warn/received', cb);
            },
        },
        story: {
            onPublished: (cb: () => void) => {
                on('/story/published', cb);
            },
            onError: (cb: () => void) => {
                on('/story/error', cb);
            },
        },
    };
}

export const socket = initSocket();

if (browser) {
    const originalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
        socket?.checkConnection();
        return originalFetch(...args);
    };
}
